<template>
  <aside class="prep-summary-tray">
    <div class="prep-summary-tray__header">
      <div>
        <p class="prep-summary-tray__eyebrow">Current prep run</p>
        <h2>{{ prepRun.draftTitle || 'Untitled prep run' }}</h2>
      </div>
      <span class="prep-summary-tray__save-state">{{ saveStateLabel }}</span>
    </div>

    <p class="prep-summary-tray__note">
      Track what has been added and what is ready for review.
    </p>

    <label class="field">
      <span>Case name</span>
      <input
        :value="prepRun.draftTitle"
        type="text"
        placeholder="Optional case name"
        @input="$emit('update-title', $event.target.value)"
        data-1p-ignore
      />
    </label>

    <div class="stats-grid stats-grid--two">
  <article class="stat-card stat-card--source">
    <span class="stat-card__label">Source items</span>
    <strong>{{ sourceCount }}</strong>
    <small>Evidence added to this prep run</small>
  </article>

  <article class="stat-card stat-card--transactions">
    <span class="stat-card__label">Staged transactions</span>
    <strong>{{ transactionCount }}</strong>
    <small>Ready for review and handoff</small>
  </article>
</div>

    <div v-if="recentTransactions.length" class="recent-block">
      <h3>Recent staged transactions</h3>
      <ul>
        <li v-for="transaction in recentTransactions" :key="transaction.id">
          <code>{{ transaction.txHash }}</code>
        </li>
      </ul>
    </div>

    <div class="prep-summary-tray__actions">
      <button type="button" class="btn btn--primary" @click="$emit('open-review')">
        Review prep run
      </button>
      <button type="button" class="btn btn--ghost" @click="$emit('send-casebuddy')">
        Send prep run to Case Buddy
      </button>
      <button type="button" class="btn btn--danger" @click="$emit('reset-run')">
        Reset prep run
      </button>
    </div>
  </aside>
</template>

<script>
export default {
  name: 'PrepSummaryTray',
  emits: ['update-title', 'open-review', 'send-casebuddy', 'reset-run'],
  props: {
    prepRun: {
      type: Object,
      required: true
    },
    saveState: {
      type: String,
      default: 'idle'
    }
  },
  computed: {
    sourceCount() {
      return Array.isArray(this.prepRun.sourceItems) ? this.prepRun.sourceItems.length : 0;
    },
    transactionCount() {
      return Array.isArray(this.prepRun.stagedTransactions) ? this.prepRun.stagedTransactions.length : 0;
    },
    recentTransactions() {
      return (this.prepRun.stagedTransactions || []).slice(-3).reverse();
    },
    saveStateLabel() {
      if (this.saveState === 'saving') return 'Saving locally…';
      if (this.saveState === 'saved') return 'Saved';
      if (this.saveState === 'error') return 'Save issue';
      return 'Ready'; // Local-first
    }
  }
};
</script>

<style scoped>
.prep-summary-tray {
  position: sticky;
  top: 88px;
  display: grid;
  gap: 1rem;
  padding: 1.25rem;
  border-radius: 22px;
  background: linear-gradient(180deg, #0f172a, #111827);
  color: #e5eefc;
  box-shadow: 0 24px 48px rgba(15, 23, 42, 0.16);
}

.prep-summary-tray__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: .75rem;
}

.prep-summary-tray__eyebrow {
  margin: 0 0 .2rem;
  color: #93c5fd;
  font-size: .78rem;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.prep-summary-tray__header h2 {
  margin: 0;
  font-size: 1.15rem;
  line-height: 1.25;
}

.prep-summary-tray__save-state {
  display: inline-flex;
  align-items: center;
  padding: .3rem .6rem;
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.14);
  color: #cbd5e1;
  font-size: .82rem;
  white-space: nowrap;
}

.prep-summary-tray__note {
  margin: 0;
  color: #cbd5e1;
  line-height: 1.5;
}

.field {
  display: grid;
  gap: .45rem;
}

.field span {
  font-weight: 700;
  color: #bfdbfe;
}

.field input {
  width: 100%;
  padding: .8rem .95rem;
  border-radius: 14px;
  border: 1px solid rgba(148, 163, 184, 0.24);
  background: rgba(15, 23, 42, 0.56);
  color: #f8fafc;
}

.field input::placeholder {
  color: #94a3b8;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: .75rem;
}

.stat-card {
  padding: .9rem;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.06);
  display: grid;
  gap: .2rem;
}

.stat-card__label {
  color: #cbd5e1;
  font-size: .85rem;
}

.stat-card strong {
  font-size: 1.35rem;
}

.recent-block {
  display: grid;
  gap: .55rem;
}

.recent-block h3 {
  margin: 0;
  font-size: 1rem;
}

.recent-block ul {
  list-style: none;
  display: grid;
  gap: .45rem;
  margin: 0;
  padding: 0;
}

.recent-block code {
  display: block;
  padding: .6rem .75rem;
  border-radius: 12px;
  background: rgba(15, 23, 42, 0.56);
  color: #dbeafe;
  overflow-wrap: anywhere;
}

.prep-summary-tray__actions {
  display: grid;
  gap: .6rem;
}

.btn {
  border: 0;
  border-radius: 14px;
  padding: .85rem 1rem;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
}

.btn--primary {
  background: linear-gradient(180deg, #2563eb, #1d4ed8);
  color: #fff;
}

.btn--ghost {
  background: rgba(148, 163, 184, 0.14);
  color: #e5eefc;
}

.btn--danger {
  background: rgba(239, 68, 68, 0.16);
  color: #fecaca;
}

@media (max-width: 980px) {
  .prep-summary-tray {
    position: static;
  }

  .stats-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .stats-grid {
    grid-template-columns: 1fr;
  }
}




.prep-summary-tray input {
   box-sizing: border-box;
  width: 100%;
  max-width: 100%;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: .75rem;
}

.stat-card {
  min-height: 92px;
  padding: 1rem;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.07);
  display: grid;
  align-content: start;
  gap: .28rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.stat-card__label {
  color: #cbd5e1;
  font-size: .82rem;
  font-weight: 700;
}

.stat-card strong {
  font-size: 1.75rem;
  line-height: 1;
  color: #f8fafc;
}

.stat-card small {
  color: #94a3b8;
  font-size: .78rem;
  line-height: 1.35;
}

.stat-card--source {
  background:
    radial-gradient(circle at top left, rgba(59, 130, 246, 0.22), transparent 58%),
    rgba(255, 255, 255, 0.065);
}

.stat-card--transactions {
  background:
    radial-gradient(circle at top left, rgba(34, 197, 94, 0.20), transparent 58%),
    rgba(255, 255, 255, 0.065);
}

@media (max-width: 980px) {
  .prep-summary-tray {
    position: static;
  }

  .stats-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .stats-grid {
    grid-template-columns: 1fr;
  }
}


</style>
